import React from 'react'
import { graphql } from 'gatsby'

// Ours
import getCtaIdFromSelect from '../utils/get-ctaid-from-select'
import SEO from '../components/SEO'
import HeaderAbstract from '../components/HeaderAbstract'
import MainBlocksMap from '../components/MainBlocksMap'
import { Text } from '../components/MainBlocks'
import CallToAction from '../components/CallToAction'
import ApproachExpertiseListing from '../components/ApproachExpertiseListing'
import ApproachServicesList from '../components/ApproachServicesList'

const handleBeforeMain = node => {
  if (!node || !node.slug) {
    return null
  }

  switch (node.slug) {
    case 'services-and-approach':
      return (
        <React.Fragment>
          <ApproachExpertiseListing {...node} />
          <ApproachServicesList />
        </React.Fragment>
      )
    default:
      return null
  }
}

const handleAfterMain = node => {
  return null
}

const handleHeader = node => {
  if (!node || !node.slug) {
    return null
  }

  switch (node.slug) {
    case 'services-and-approach':
      return <h1 className="hide">{node.title}</h1>
    default:
      return (
        <HeaderAbstract
          title={node.title}
          subtitle={node.main.subtitle || node.details.description}
          bgImage={
            node.featuredImage
              ? node.featuredImage.node.localFile.childImageSharp.resize.src
              : null
          }
        />
      )
  }
}

const WPPage = props => {
  const data = props.data
  const pageNode = data.wpPage

  let beforeMain = handleBeforeMain(pageNode)
  let afterMain = handleAfterMain(pageNode)
  let chosenCta = getCtaIdFromSelect(data.wpPage.details)
  let header = handleHeader(pageNode)

  // Support for old pages – Should be able to remove this
  // once data is ported, and then maybe hide WYSIWYG content input
  // in WP as well, but leaving for now.
  let oldContentStructure =
    pageNode.content && pageNode.content !== '' ? (
      <Text text={pageNode.content} />
    ) : null

  return (
    <div className="WPPage bg-white">
      <SEO
        title={pageNode.title}
        description={pageNode.details.description || pageNode.main.subtitle}
        featuredImage={pageNode.featuredImage}
      />
      <article>
        {header}
        <MainBlocksMap
          beforeBlocks={beforeMain}
          blocks={data.wpPage.main.mainContent || []}
          afterBlocks={afterMain}
        />
        {oldContentStructure}
      </article>
      <CallToAction ctaId={chosenCta} />
    </div>
  )
}

export default WPPage

export const pageQuery = graphql`
  query wpPageById($id: String!) {
    wpPage(id: { eq: $id }) {
      id
      title
      slug
      modified
      content
      databaseId
      details {
        description
        ctaSelect
      }

      parent {
        id
      }
      parentId

      featuredImage {
        node {
          id
          localFile {
            extension
            childImageSharp {
              id
              fluid(maxWidth: 1280) {
                ...GatsbyImageSharpFluid_withWebp_noBase64
              }
              resize(width: 1920, height: 1200, cropFocus: CENTER) {
                src
              }
            }
          }
        }
      }
      main {
        subtitle
        ... on WpPage_Main {
          mainContent {
            ... on WpPage_Main_MainContent_SectionHeading {
              fieldGroupName
              text
            }
            ... on WpPage_Main_MainContent_Text {
              fieldGroupName
              sectionHeading
              text
            }
            ... on WpPage_Main_MainContent_Image {
              fieldGroupName
              sectionHeading
              image {
                uri
                altText
                localFile {
                  childImageSharp {
                    id
                    fluid(maxWidth: 2560) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
              captionDetails {
                detail
                fieldGroupName
                label
                labelCustom
              }
              captionText
              settings {
                showFullWidth
              }
            }
            ... on WpPage_Main_MainContent_TwoColumn {
              fieldGroupName
              image {
                altText
                localFile {
                  childImageSharp {
                    id
                    fluid(maxWidth: 1280) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
              content {
                subhead
                text
                callToAction {
                  target
                  title
                  url
                }
              }
              settings {
                bg
                color
                sticky
              }
            }
            ... on WpPage_Main_MainContent_ThreeColumn {
              fieldGroupName
              sectionHeading
              columns {
                bigNumber
                image {
                  altText
                  localFile {
                    childImageSharp {
                      id
                      resize(width: 1280, height: 1280, cropFocus: CENTER) {
                        src
                      }
                    }
                  }
                }
                subhead
                text
              }
              settings {
                showBigNumbers
                showImages
                # showImagesStylized
              }
            }
            ... on WpPage_Main_MainContent_PartnerLogos {
              fieldGroupName
              sectionHeading
              partnerLogos {
                altText
                id
                localFile {
                  childImageSharp {
                    id
                    fluid(maxWidth: 600) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
            ... on WpPage_Main_MainContent_Testimonials {
              fieldGroupName
              testimonials {
                author
                organization
                quote
              }
            }
            ... on WpPage_Main_MainContent_Project {
              fieldGroupName
              project {
                ... on WpProject {
                  title
                  uri
                  projectDetails {
                    projectStatus
                  }
                  details {
                    description
                  }
                  featuredImage {
                    node {
                      id
                      localFile {
                        extension
                        childImageSharp {
                          id
                          resize(width: 1280, height: 1280, cropFocus: CENTER) {
                            src
                          }
                        }
                      }
                    }
                  }
                  featuredImagePortrait {
                    featuredMediaPortrait {
                      localFile {
                        extension
                        childImageSharp {
                          id
                          fluid(maxWidth: 1280) {
                            ...GatsbyImageSharpFluid_withWebp_noBase64
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on WpPage_Main_MainContent_Gallery {
              captionText
              settings {
                showFullWidth
              }
              fieldGroupName
              sectionHeading
              gallery {
                altText
                localFile {
                  extension
                  childImageSharp {
                    id
                    fluid(maxWidth: 2560) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`
